import React, { FunctionComponent, MouseEventHandler, ReactElement, useRef, useState } from 'react';
import { WatchingEntity } from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { RouterLocation } from '@funtech-int/rival';
import classnames from 'classnames';
import { Picture, createPictureFactory, Tooltip } from '@funtech-int/yango-shared';
import { useIntl } from 'react-intl';

import * as CardKit from '@/shared/components/CardKit';
import { useMovieExpirationText } from '@/shared/hooks/useMovieExpiration';
import { useHasSubscription } from '@/shared/hooks/useHasSubscription';
import { useI18n } from '@/shared/hooks/useI18n';
import { formatDuration } from '@/shared/lib/formatTime';

import styles from './_Watching.module.css';

const pictureFactory = createPictureFactory({
    fallback: {
        srcSet: {
            '1x': '375x234',
            '2x': '640x360', // actually 1.7x but this is maximum available size
        },
        src: '375x234',
    },
});

interface TvSeries {
    seasonNumber: number | undefined;
    seriesNumber: number;
}

interface StartContentParams {
    contentId: string;
    movieId?: string;
    tvSeries?: TvSeries;
}

interface WatchingHistoryCardProps {
    entity: WatchingEntity;
    className?: string;
    location: RouterLocation<any, any>;
    startContent: (params: StartContentParams) => void;
    onNavigated: VoidFunction;
    onShow: VoidFunction;
    onRemove?: VoidFunction;
    onCloseRemoveHint?: VoidFunction;
    withHint?: boolean;
    withVisibleRemoveButton?: boolean;
}

export const Watching: FunctionComponent<WatchingHistoryCardProps> = ({
    entity,
    className,
    location,
    startContent,
    onNavigated,
    onShow,
    onRemove,
    onCloseRemoveHint,
    withHint,
    withVisibleRemoveButton,
}) => {
    const intl = useIntl();

    const isPurchased = Boolean(entity.viewOption?.isPurchased);
    const isWatchable = Boolean(entity.viewOption?.isWatchable);

    const hasSubscription = useHasSubscription();

    const watchPeriod = entity.viewOption?.watchPeriod;

    const handleClick: MouseEventHandler = (event) => {
        if (isPurchased || !isWatchable) {
            event.preventDefault();
        }

        if (isPurchased) {
            startContent({
                tvSeries: entity.tvSeries ?? undefined,
                movieId: entity.movieId ?? undefined,
                contentId: entity.contentId,
            });
        }

        onNavigated();
    };

    const i18n = useI18n();

    const expirationText = useMovieExpirationText(
        watchPeriod?.watchPeriodStatus,
        watchPeriod?.timeToExpire
    );

    const subtitle =
        entity.tvSeries &&
        typeof entity.tvSeries?.seasonNumber === 'number' &&
        typeof entity.tvSeries?.seriesNumber === 'number'
            ? i18n('season_and_series', {
                  seasonNumber: entity.tvSeries?.seasonNumber,
                  episodeNumber: entity.tvSeries?.seriesNumber,
              })
            : undefined;

    const { watchProgressPosition } = entity;
    const { duration } = entity;

    let purchaseBadge: ReactElement | undefined;
    let expirationInfo: ReactElement | undefined;
    let waitingBadge: ReactElement | undefined;
    let actionsSlot: ReactElement | undefined;

    if (hasSubscription && !entity.viewOption?.isPurchased && entity.viewOption?.isWatchable) {
        purchaseBadge = (
            <CardKit.PurchaseBadge
                ariaLabel={i18n('card__purchasable')}
                badge={entity.viewOption?.subscriptionBadge}
            />
        );
    }

    if (expirationText) {
        expirationInfo = (
            <CardKit.ExpirationLabel
                withIcon
                variant={expirationText.isHighlighted ? 'warning' : 'default'}
            >
                {expirationText.text}
            </CardKit.ExpirationLabel>
        );
    }

    if (!isWatchable) {
        waitingBadge = (
            <CardKit.WaitingBadge
                text={i18n('card__waiting_unwatchable')}
                viewVariant="unwatchable"
            />
        );
    }

    const removeButtonRef = useRef<HTMLDivElement>(null);
    const [isHintVisible, setIsHintVisible] = useState(true);

    if (onRemove) {
        actionsSlot = (
            <div
                ref={removeButtonRef}
                className={classnames(styles.removeButton, {
                    [styles.visible]: withVisibleRemoveButton,
                })}
            >
                <CardKit.RemoveButton
                    onClick={onRemove}
                    ariaLabel={i18n('continue_watching__remove_button__label', {
                        title: entity.title.toString(),
                    })}
                />
                {withHint && isHintVisible && (
                    <Tooltip
                        visible
                        direction="top"
                        alignment="middle"
                        anchorRef={removeButtonRef}
                        inheritWidth={false}
                        mainOffset={32}
                        onCloseClick={() => {
                            setIsHintVisible(false);
                            onCloseRemoveHint?.();
                        }}
                    >
                        {i18n('continue_watching__remove_button__hint')}
                    </Tooltip>
                )}
            </div>
        );
    }

    const { img } = pictureFactory({
        fallbackUrl: entity.backgroundUrl?.toString(),
    });

    return (
        <CardKit.Layout
            location={location}
            onClick={handleClick}
            onShow={onShow}
            dataTid="CardWatching"
            className={classnames(styles.root, className)}
            actions={actionsSlot}
        >
            <CardKit.ImageOverlay
                image={
                    <Picture
                        className={styles.image}
                        alt={entity.title.toString()}
                        loading="lazy"
                        srcSet={img.srcSet}
                        src={img.src}
                        data-tid="AdaptiveImage"
                    />
                }
            >
                {isWatchable ? (
                    <>
                        <CardKit.Play variant={isPurchased ? 'primary' : 'plus'} />
                        {watchProgressPosition ? (
                            <CardKit.Progress
                                value={watchProgressPosition}
                                maxValue={duration}
                                dateTime={formatDuration(intl, duration)}
                                text={formatDuration(intl, duration)}
                            />
                        ) : null}
                    </>
                ) : (
                    <CardKit.Lock />
                )}
                <div className={styles.badges}>
                    {purchaseBadge}
                    {waitingBadge}
                </div>
            </CardKit.ImageOverlay>
            <CardKit.Caption
                title={entity.title.toString()}
                subtitle={subtitle}
                dot={!watchProgressPosition}
                className={styles.caption}
            />
            {expirationInfo}
        </CardKit.Layout>
    );
};
